/**
 * Mobile-only full-screen panel for the **Ballot** and **My Info** tabs.
 * Fills the space between the app bar and the bottom tab bar, and stays
 * mounted (hidden with ``visibility``) while the Chat tab is showing so the
 * ballot's data, scroll position, and expanded races survive tab switches.
 *
 * One ``SampleBallotView`` instance serves both tabs: the Ballot tab shows
 * the election tabs (contests, candidates, measures) and the My Info tab
 * shows the Voting Info section (sources, dates, officeholders). A guest
 * with no address — or a signed-in voter who deleted their last ballot —
 * gets the landing's address entry instead, as the desktop pane does.
 *
 * Renders inline (no portal) like the rest of the mobile chrome; see the
 * ``BallotSheetProvider`` doc comment.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import {
  Box,
  CircularProgress,
  GlobalStyles,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { useAuth } from '@/components/auth/AuthProvider';
import BallotStatusButton from '@/components/ballot/BallotStatusButton';
import SampleBallotView from '@/components/ballot/SampleBallotView';
import GuestLandingPage from '@/components/landing/GuestLandingPage';
import useGuestAddress from '@/hooks/useGuestAddress';
import useGuestBallotDownload, { type BallotDownloadSource } from '@/hooks/useGuestBallotDownload';
import { useDirection } from '@/hooks/useDirection';
import { MOBILE_TOOLBAR_HEIGHT_PX, useBallotSheet } from '@/providers/BallotSheetProvider';
import { primitiveColors, semanticColors } from '@/theme/tokens';
import { getInputModality, installInputModalityTracker } from '@/utils/inputModality';

import { MOBILE_TAB_BAR_HEIGHT_VAR } from './MobileTabBar';

// Slide timings for the pager feel: the whole panel when entering from / leaving
// to Chat, a short shift of the content when moving between Ballot and My Info.
const PANEL_SLIDE_MS = 240;
const SECTION_SLIDE_MS = 200;
const SECTION_SLIDE_PX = 40;
// A tab switch this soon after mount is the route settling its opening tab.
const OPENING_TAB_GRACE_MS = 600;

interface MobileBallotPanelProps {
  /** Data source for ``SampleBallotView``: the signed-in voter's records or
   * the national snapshot. */
  mode: 'personal' | 'national';
  /** Show the landing's address entry instead of a ballot — no address
   * anywhere yet, or the voter deleted their last saved ballot. */
  showGuestLanding: boolean;
  /** A ballot is mid-build; announced to screen readers from a live region
   * that outlives the landing ↔ ballot swap below. */
  ballotBuilding: boolean;
}

const MobileBallotPanel: React.FC<MobileBallotPanelProps> = ({
  mode,
  showGuestLanding,
  ballotBuilding,
}) => {
  const { t } = useTranslation('common');
  const { t: tBallot } = useTranslation('ballot');
  const { isAuthenticated } = useAuth();
  const { activeTab, headerOffsetPx, locality, landingFullScreen } = useBallotSheet();
  const { address: guestAddress } = useGuestAddress();

  const showing = activeTab !== 'chat';
  // The landing takes the app bar's place as well as the tab bar's, so the
  // panel starts where the toolbar would have: below the notification banner
  // (the rest of ``headerOffsetPx``) and nothing else.
  const panelTopPx = landingFullScreen
    ? Math.max(0, headerOffsetPx - MOBILE_TOOLBAR_HEIGHT_PX)
    : headerOffsetPx;
  // Which section the always-mounted panel renders. On a ballot tab it follows
  // that tab synchronously, so the entrance slide's first painted frame already
  // carries the right heading and content — the motion ``useLayoutEffect``
  // below reveals the panel before any passive effect could correct it. On Chat
  // it falls back to the last ballot section, so the hidden panel keeps showing
  // what the voter left: flipping it back to ``ballot`` behind their back would
  // swap the content — losing that section's scroll position — and re-title the
  // panel mid exit-slide. The state only has to be current by the time Chat
  // reads it, which the effect below guarantees: it commits while that section
  // is still the visible one.
  const [lastBallotSection, setLastBallotSection] = useState<'ballot' | 'info'>(
    activeTab === 'info' ? 'info' : 'ballot'
  );
  useEffect(() => {
    if (activeTab !== 'chat') setLastBallotSection(activeTab === 'info' ? 'info' : 'ballot');
  }, [activeTab]);
  const section: 'ballot' | 'info' =
    activeTab === 'chat' ? lastBallotSection : activeTab === 'info' ? 'info' : 'ballot';
  const title = section === 'info' ? tBallot('tabs.votingInfo') : t('ballotPreviewLabel');

  // The header controls belong to the Ballot tab; My Info keeps just the title
  // and locality.
  const onBallotSection = section === 'ballot' && !showGuestLanding;
  // Status control for a real ballot: personal (signed-in) or a guest with a
  // saved address. Hidden for the pure national snapshot and the landing.
  const showStatus =
    onBallotSection && (mode === 'personal' || Boolean(guestAddress?.formatted_address));
  // The export endpoint renders the signed-in account's ballot, so a guest with
  // a ballot gets the same button and it opens sign in; the national view gets
  // none.
  const downloadRef = useRef<(() => void) | null>(null);
  const guestDownload = useGuestBallotDownload();
  const onDownload: ((source: BallotDownloadSource) => void) | undefined = !onBallotSection
    ? undefined
    : mode === 'personal' && isAuthenticated
      ? () => downloadRef.current?.()
      : guestDownload;
  const [isDownloading, setIsDownloading] = useState(false);

  // Focus management for switches triggered from inside the content rather
  // than the tab bar: "Full profile" hides this panel with its button still
  // focused, and the "see voting info" chip unmounts with the election panel.
  // Keyboard users get a landing spot — this panel's heading on a section
  // change (unless they are on the tab bar, which keeps focus where it is),
  // the Chat tab on a return to Chat. Touch users only get a stranded control
  // blurred: moving focus for them draws a focus ring on the heading or the
  // tab, which reads as a stray outline. Transitions only — never on mount.
  const panelRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const prevTabRef = useRef(activeTab);
  // Whether the panel is painted at all — see the motion block below, which
  // owns it. Declared here because the focus effect gates on it.
  const [rendered, setRendered] = useState(showing);
  useEffect(() => installInputModalityTracker(), []);
  useEffect(() => {
    const prev = prevTabRef.current;
    if (prev === activeTab || typeof document === 'undefined') return;
    // The panel becomes visible in a follow-up render; a hidden heading
    // cannot take focus, so wait for that render.
    if (activeTab !== 'chat' && !rendered) return;
    prevTabRef.current = activeTab;
    const active = document.activeElement;
    const keyboard = getInputModality() === 'keyboard';
    const focusLost = !active || active === document.body;
    const focusInPanel = Boolean(panelRef.current?.contains(active));
    if (activeTab === 'chat') {
      if (keyboard && (focusLost || focusInPanel)) {
        document
          .querySelector<HTMLElement>('nav [data-vm-tab="chat"]')
          ?.focus({ preventScroll: true });
      } else if (focusInPanel && active instanceof HTMLElement) {
        active.blur();
      }
      return;
    }
    if (!keyboard) return;
    const onTabBar = Boolean(active?.closest('nav [data-vm-tab]'));
    if (!onTabBar) headingRef.current?.focus({ preventScroll: true });
  }, [activeTab, rendered]);

  // The landing ↔ ballot swap is the other way this panel's contents change,
  // and it happens with the tab unchanged (a guest enters an address, or clears
  // one), so the effect above never sees it. Both directions unmount whatever
  // held focus — the "See my ballot" button, or the app bar and hamburger that
  // this state hides — and focus reverts to the body. Give keyboard users the
  // heading, or the panel itself while the landing hides the header band.
  const prevLandingFullScreenRef = useRef(landingFullScreen);
  useEffect(() => {
    const prev = prevLandingFullScreenRef.current;
    prevLandingFullScreenRef.current = landingFullScreen;
    if (prev === landingFullScreen || typeof document === 'undefined') return;
    if (getInputModality() !== 'keyboard') return;
    const active = document.activeElement;
    const stranded =
      !active || active === document.body || !document.documentElement.contains(active);
    if (!stranded) return;
    const heading = headingRef.current;
    if (heading && heading.offsetParent !== null) heading.focus({ preventScroll: true });
    else panelRef.current?.focus({ preventScroll: true });
  }, [landingFullScreen]);

  // Pager motion. Chat sits to the left of Ballot, which sits to the left of
  // My Info, so the panel slides in from the right when leaving Chat and back
  // out to the right when returning; between Ballot and My Info the content
  // shifts a short distance in the direction of travel.
  //
  // Two pieces of state keep the chat and the panel from ever both being
  // missing or both painting at rest: ``rendered`` keeps the panel visible
  // through its exit slide and hides it only once the slide is done, and
  // ``chatHidden`` takes the chat out of the accessibility tree only once the
  // panel has fully slid in, and puts it back the instant a return to Chat
  // starts. ``useLayoutEffect`` so the panel is visible on the very first
  // animated frame.
  const [chatHidden, setChatHidden] = useState(showing);
  const contentRef = useRef<HTMLDivElement>(null);
  const panelAnimationRef = useRef<Animation | null>(null);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  // The panel enters from the side the later tabs sit on, which ``dir="rtl"``
  // flips. ``translateX`` is physical — unlike the flex row it slides over — so
  // the slides mirror explicitly or they run backwards in Arabic, Persian, and
  // Urdu. Held in a ref so switching language mid-session does not re-run the
  // motion effect below and replay a slide the voter did not ask for.
  const dir = useDirection();
  const axisRef = useRef(dir === 'rtl' ? -1 : 1);
  axisRef.current = dir === 'rtl' ? -1 : 1;
  const prevTabForMotionRef = useRef(activeTab);
  // The chat route picks its opening tab right after mount (a guest with no
  // address opens on Ballot). That is a starting state, not a navigation, so
  // it appears in place rather than sliding in.
  const mountedAtRef = useRef(typeof performance !== 'undefined' ? performance.now() : 0);
  useLayoutEffect(() => {
    const prev = prevTabForMotionRef.current;
    prevTabForMotionRef.current = activeTab;
    if (prev === activeTab) return;
    const panel = panelRef.current;
    // A switch mid-slide takes over from whatever was animating. Cancelling
    // also keeps the cancelled slide's completion handler from running late.
    panelAnimationRef.current?.cancel();
    panelAnimationRef.current = null;
    const settlingOpeningTab = performance.now() - mountedAtRef.current < OPENING_TAB_GRACE_MS;
    if (!panel || typeof panel.animate !== 'function' || settlingOpeningTab) {
      setRendered(activeTab !== 'chat');
      setChatHidden(activeTab !== 'chat');
      return;
    }
    const duration = reduceMotion ? 0 : PANEL_SLIDE_MS;
    if (activeTab === 'chat') {
      // Reveal the chat underneath right away and slide the panel off over it.
      setChatHidden(false);
      const exit = panel.animate(
        [{ transform: 'translateX(0)' }, { transform: `translateX(${100 * axisRef.current}%)` }],
        { duration, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
      );
      panelAnimationRef.current = exit;
      const finish = () => {
        if (panelAnimationRef.current !== exit) return;
        panelAnimationRef.current = null;
        // Hide the panel synchronously BEFORE releasing the forward fill.
        // A plain state update here flushes on a later task, and dropping
        // the fill first paints the panel one frame at rest over the chat —
        // the "flash" of ballot on the chat screen.
        flushSync(() => setRendered(false));
        exit.cancel();
      };
      exit.onfinish = finish;
      // Animations pause in a background tab; the timer guarantees the panel
      // still hides once the switch is due.
      const fallback = window.setTimeout(finish, duration + 100);
      exit.oncancel = () => window.clearTimeout(fallback);
      return;
    }
    setRendered(true);
    if (prev === 'chat') {
      const enter = panel.animate(
        [{ transform: `translateX(${100 * axisRef.current}%)` }, { transform: 'translateX(0)' }],
        { duration, easing: 'cubic-bezier(0.2, 0, 0, 1)' }
      );
      panelAnimationRef.current = enter;
      enter.onfinish = () => {
        if (panelAnimationRef.current !== enter) return;
        panelAnimationRef.current = null;
        setChatHidden(true);
      };
      return;
    }
    const fromPx = (activeTab === 'info' ? SECTION_SLIDE_PX : -SECTION_SLIDE_PX) * axisRef.current;
    contentRef.current?.animate(
      [
        { transform: `translateX(${fromPx}px)`, opacity: 0.4 },
        { transform: 'translateX(0)', opacity: 1 },
      ],
      { duration: reduceMotion ? 0 : SECTION_SLIDE_MS, easing: 'ease-out' }
    );
  }, [activeTab, reduceMotion]);

  return (
    <>
      {/* While a ballot tab is showing, take the chat out of the accessibility
          tree and pointer flow — this panel covers it visually, and without
          this a screen reader would still walk the hidden transcript. Applied
          once the panel has slid in, lifted the instant it starts sliding out,
          so the chat is on screen under the moving panel. Scoped to the phone
          breakpoint; the desktop panel never overlays chat. */}
      {chatHidden && (
        <GlobalStyles
          styles={(theme) => ({
            [theme.breakpoints.down('md')]: {
              // Toolpad renders the content area as a bare ``<main>`` (no
              // ``role`` attribute); match either spelling.
              'main.MuiBox-root, .MuiBox-root[role="main"]': { visibility: 'hidden' },
            },
          })}
        />
      )}
      {/* The landing carries its own sign-in and language controls, so the app
          bar would only repeat them over a screen that reads as the standalone
          landing page. ``display: none`` (not ``visibility``) so it gives up
          its space too — the panel moves up into it via ``panelTopPx``.
          ``!important`` beats the layout's own ``& .MuiAppBar-root`` sx rule,
          which is a class-descendant selector and outranks this global one. */}
      {landingFullScreen && (
        <GlobalStyles
          styles={(theme) => ({
            [theme.breakpoints.down('md')]: {
              // The app bar, and the hamburger strip that sits beside it as a
              // fixed sibling rather than inside it.
              '.MuiAppBar-root, #main-menu-button': { display: 'none !important' },
            },
          })}
        />
      )}
      <Box
        ref={panelRef}
        role="region"
        // "Ballot preview" overclaims while the address entry is up — name the
        // landmark after the tab the voter is on, which is true either way and
        // still distinguishes Ballot from My Info.
        aria-label={
          showGuestLanding ? t(section === 'info' ? 'bottomNav.info' : 'bottomNav.ballot') : title
        }
        // Focus target of last resort for the landing ↔ ballot swap above.
        tabIndex={-1}
        sx={{
          position: 'fixed',
          top: panelTopPx,
          left: 0,
          right: 0,
          // Stop at the tab bar's top edge; when the bar hides for the
          // keyboard its variable drops to 0 and the panel extends down —
          // onto the keyboard, unless the inset is taken into account.
          // Chromium overlays the keyboard without shrinking the layout
          // viewport, so ``--keyboard-inset-px`` (from BallotSheetProvider) is
          // the only signal that the bottom of this fixed panel is covered;
          // without it the landing's address field and buttons sit under it.
          // ``max()`` because the bar and the keyboard are never both up.
          bottom: `calc(max(var(--keyboard-inset-px, 0px), env(safe-area-inset-bottom)) + var(${MOBILE_TAB_BAR_HEIGHT_VAR}, 0px))`,
          zIndex: (theme) => theme.zIndex.appBar,
          // Mobile-only. On md+ the ballot lives in the right-side panel.
          display: { xs: 'flex', md: 'none' },
          flexDirection: 'column',
          backgroundColor: primitiveColors.blue1,
          // Stays visible through the exit slide; taps fall through to the
          // chat the moment the switch happens.
          visibility: rendered ? 'visible' : 'hidden',
          pointerEvents: showing ? 'auto' : 'none',
          overflow: 'hidden',
        }}
      >
        {/* Header band — the title, locality, download, and status control that
            used to ride on the sheet's teal grabber. Same dark surface as the
            ballot's address row below so the two read as one header. Absent
            while the landing's address entry is showing: that screen brings
            its own logo row and headline. */}
        <Box
          sx={{
            flexShrink: 0,
            display: showGuestLanding ? 'none' : 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            pt: '10px',
            pb: '6px',
            backgroundColor: semanticColors.ballotElectionCard,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              ref={headingRef}
              component="h1"
              tabIndex={-1}
              noWrap
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700,
                fontSize: 16,
                lineHeight: 1.25,
                color: primitiveColors.white,
                borderRadius: '4px',
                '&:focus-visible': {
                  outline: `2px solid ${primitiveColors.white}`,
                  outlineOffset: '2px',
                },
              }}
            >
              {title}
            </Typography>
            {locality && !showGuestLanding && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: '2px' }}>
                <LocationOnIcon
                  sx={{ fontSize: 14, color: primitiveColors.white, flexShrink: 0 }}
                  aria-hidden="true"
                />
                <Typography
                  noWrap
                  sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 500,
                    fontSize: 12,
                    lineHeight: 1.2,
                    color: primitiveColors.white,
                  }}
                >
                  {locality}
                </Typography>
              </Box>
            )}
          </Box>
          {onDownload && (
            <IconButton
              // For a guest the button opens sign in, which the tooltip (and so the
              // accessible description) says.
              title={
                isAuthenticated ? tBallot('menu.download') : tBallot('menu.downloadSignInHint')
              }
              aria-label={
                isDownloading ? tBallot('menu.generatingPdf') : tBallot('menu.downloadAriaLabel')
              }
              aria-busy={isDownloading}
              // ``aria-disabled`` + a click guard rather than the native
              // attribute, which would blur the button mid-download and
              // strand keyboard focus.
              aria-disabled={isDownloading}
              onClick={() => {
                if (!isDownloading) onDownload('header');
              }}
              sx={{
                // 44px target on the dark band.
                width: 44,
                height: 44,
                color: primitiveColors.white,
                '&:hover': { backgroundColor: semanticColors.overlayWhite10 },
              }}
            >
              {isDownloading ? (
                <CircularProgress size={18} sx={{ color: primitiveColors.white }} />
              ) : (
                <ArrowDownTrayIcon width={20} height={20} />
              )}
            </IconButton>
          )}
          {showStatus && (
            // The header's own download button covers download in tab view.
            <BallotStatusButton condensed />
          )}
        </Box>

        {/* Persistent live region (stable across the landing ↔ ballot swap) so
            screen readers hear when a ballot starts building after an address
            submit. */}
        <Box
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={{
            position: 'absolute',
            width: 1,
            height: 1,
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
          }}
        >
          {ballotBuilding ? tBallot('loading.building') : ''}
        </Box>

        {/* Absolute inner box gives ``SampleBallotView``'s ``height: 100%`` a
            definite height to resolve against; the view owns its own scroll.
            It is also what shifts on a Ballot ↔ My Info switch. */}
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
          <Box ref={contentRef} sx={{ position: 'absolute', inset: 0 }}>
            {showGuestLanding ? (
              <GuestLandingPage embedded showTopControls={landingFullScreen} />
            ) : (
              <SampleBallotView
                mode={mode}
                mobileSection={section}
                downloadRef={downloadRef}
                onIsDownloadingChange={setIsDownloading}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default MobileBallotPanel;
